import type { ApiToken, Card, CardGroup, CardSummary, Document, RegistryConflict, SystemTag, TagDef, TagValue, TokenScope } from "./types";

const BASE = "/api";
const TOKEN_KEY = "cardlake_token";

// The backend speaks camelCase on the wire (serde `rename_all = "camelCase"`),
// so the JSON matches our types verbatim — no key conversion. This is load-
// bearing: a recursive rename would also rewrite user-defined tag-map keys.

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/// Attach the bearer token (if any) to a request's headers.
function authHeaders(extra?: HeadersInit): Record<string, string> {
  const headers: Record<string, string> = { ...(extra as Record<string, string> | undefined) };
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

/// Any 401 means our session is gone (server restart, expired, or logged out
/// elsewhere): drop the token and reload, which lands the user on the login
/// screen. Never returns — it throws after kicking off the reload.
function handleUnauthorized(): never {
  clearToken();
  window.location.reload();
  throw new Error("Unauthorized");
}

// A failed HTTP request. Carries the status so callers can branch (e.g. the
// save-queue treats 409 as an external-edit conflict). The message keeps the
// `"<status> <statusText>: <body>"` shape that reportError's prefix-strip
// expects.
export class HttpError extends Error {
  constructor(public status: number, statusText: string, body: string) {
    super(`${status} ${statusText}: ${body}`);
    this.name = "HttpError";
  }
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { ...init, headers: authHeaders(init?.headers) });
  if (res.status === 401) handleUnauthorized();
  if (!res.ok) {
    const text = await res.text();
    throw new HttpError(res.status, res.statusText, text);
  }
  return (await res.json()) as T;
}

/// Log in and persist the session token. Throws on bad credentials; the error
/// message is intentionally generic (the server never says which part failed).
export async function login(user: string, password: string): Promise<void> {
  const res = await fetch(`${BASE}/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ user, password }),
  });
  if (!res.ok) {
    throw new Error("Invalid username or password");
  }
  const data = (await res.json()) as { token: string; user: string };
  setToken(data.token);
}

export async function fetchCard(id: string): Promise<Card> {
  return request<Card>(`${BASE}/card/${encodeURIComponent(id)}`);
}

// Cheap poll: the card's current state hash only (served from the in-memory
// index, no card body). Used to detect an external edit before our next save.
export async function fetchCardHash(id: string): Promise<string> {
  const r = await request<{ stateHash: string }>(`${BASE}/card/${encodeURIComponent(id)}/hash`);
  return r.stateHash;
}

export type PutCardArgs = {
  id?: string;
  lastStateHash?: string;
  tags?: Record<string, TagValue>;
  content?: Document;
};

export type PutCardResult = { id: string; stateHash: string };

export async function putCard(args: PutCardArgs): Promise<PutCardResult> {
  return request<PutCardResult>(`${BASE}/card`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(args),
  });
}

// The generic listing: a Tantivy text `search` pre-filter plus arbitrary CEL
// `filter`/`orderBy`/`groupBy`. Always returns groups (one with group=null when
// ungrouped). A 422 (bad/un-evaluable expression) surfaces as a thrown Error
// whose message carries the backend reason.
export type ListParams = {
  search?: string;
  filter?: string;
  orderBy?: string;
  orderDesc?: boolean;
  // Rank search results by where the term first appears. Mutually exclusive
  // with orderBy (the backend rejects both at once).
  orderByRelevance?: boolean;
  groupBy?: string;
  limit?: number;
};

export async function listCards(params: ListParams = {}): Promise<CardGroup[]> {
  const qs = new URLSearchParams();
  if (params.search) qs.set("search", params.search);
  if (params.filter) qs.set("filter", params.filter);
  if (params.orderBy) qs.set("orderBy", params.orderBy);
  if (params.orderDesc) qs.set("orderDesc", "true");
  if (params.orderByRelevance) qs.set("orderByRelevance", "true");
  if (params.groupBy) qs.set("groupBy", params.groupBy);
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  const q = qs.toString();
  return request<CardGroup[]>(q ? `${BASE}/cards?${q}` : `${BASE}/cards`);
}

export type CheckResult = { ok: boolean; error?: string };

// Validate a CEL expression against the user's registry (syntax + unknown
// fields). Used to block saving an invalid view expression.
export async function celCheck(expr: string): Promise<CheckResult> {
  return request<CheckResult>(`${BASE}/cel/check`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ expr }),
  });
}

export type SearchCardsOpts = {
  tagKey?: string;
  tagValue?: string;
  orderKey?: string;
  orderDir?: "asc" | "desc";
  orderByRelevance?: boolean;
  limit?: number;
};

// Flat text/tag search used by the topbar and template modal. Translates the
// legacy tag-key/value options into CEL and unwraps the single result group.
export async function searchCards(
  query?: string,
  opts: SearchCardsOpts = {}
): Promise<CardSummary[]> {
  const params: ListParams = {};
  if (query) params.search = query;
  if (opts.tagKey) {
    // Presence (`tag != null`) or exact string match.
    params.filter =
      opts.tagValue !== undefined
        ? `${opts.tagKey} == ${JSON.stringify(opts.tagValue)}`
        : `${opts.tagKey} != null`;
  }
  if (opts.orderKey) {
    params.orderBy = opts.orderKey;
    params.orderDesc = opts.orderDir !== "asc";
  } else if (opts.orderByRelevance) {
    params.orderByRelevance = true;
  }
  if (opts.limit !== undefined) params.limit = opts.limit;
  const groups = await listCards(params);
  return groups[0]?.cards ?? [];
}

export type GlobalConfig = { homeCardId: string | null };

export async function fetchGlobal(): Promise<GlobalConfig> {
  return request<GlobalConfig>(`${BASE}/global`);
}

export async function putGlobal(config: GlobalConfig): Promise<GlobalConfig> {
  return request<GlobalConfig>(`${BASE}/global`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(config),
  });
}

export type RegistryResult = { tags: TagDef[]; systemTags: SystemTag[]; stateHash: string };

export async function fetchRegistry(): Promise<RegistryResult> {
  return request<RegistryResult>(`${BASE}/registry`);
}

// Upsert outcome: the save landed, or it was rejected for stranded card usage
// (conflicts) or a stale hash (someone else saved first).
export type PutRegistryOutcome =
  | { status: "ok"; tags: TagDef[]; stateHash: string }
  | { status: "conflict"; conflicts: RegistryConflict[] }
  | { status: "stale" };

export async function putRegistry(
  tags: TagDef[],
  lastStateHash: string
): Promise<PutRegistryOutcome> {
  const res = await fetch(`${BASE}/registry`, {
    method: "PUT",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ tags, lastStateHash }),
  });
  if (res.status === 401) handleUnauthorized();
  if (res.ok) {
    const data = (await res.json()) as RegistryResult;
    return { status: "ok", tags: data.tags, stateHash: data.stateHash };
  }
  if (res.status === 409) {
    // Usage conflicts carry a JSON body; a stale hash is plain text.
    const text = await res.text();
    try {
      const body = JSON.parse(text) as { conflicts?: RegistryConflict[] };
      if (body.conflicts) return { status: "conflict", conflicts: body.conflicts };
    } catch {
      /* not JSON → stale */
    }
    return { status: "stale" };
  }
  const text = await res.text();
  throw new Error(`${res.status} ${res.statusText}: ${text}`);
}

export async function listTokens(): Promise<ApiToken[]> {
  return request<ApiToken[]>(`${BASE}/tokens`);
}

// The response carries the secret. The server never returns it again, so the
// caller must show it at once.
export async function createToken(name: string, scope: TokenScope): Promise<ApiToken & { token: string }> {
  return request<ApiToken & { token: string }>(`${BASE}/tokens`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, scope }),
  });
}

export async function deleteToken(id: string): Promise<void> {
  const res = await fetch(`${BASE}/tokens/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (res.status === 401) handleUnauthorized();
  if (!res.ok) {
    throw new Error(`${res.status} ${res.statusText}`);
  }
}

export async function deleteCard(id: string): Promise<void> {
  const res = await fetch(`${BASE}/card/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (res.status === 401) handleUnauthorized();
  if (!res.ok) {
    throw new Error(`${res.status} ${res.statusText}`);
  }
}
